import { Store } from '@ngxs/store';
import { PageEvent } from '@angular/material/paginator';
import { Draft, Immutable, produce } from 'immer';
import { Observable } from 'rxjs';
import { Page } from '../models/page';
import { BasePaginatedApiService } from '../services/base-paginated-api.service';
import { NavigateService } from '../services/navigate.service';
import { NgxsStateFacade } from './ngxs-state-facade';

export interface BasePaginatedTableStateModel<T> {
  pageItems: Array<T>;
  paginatorIndex: number;
  pageSize: number;
  totalCount: number;
  filterForm: {
    model: {
      [key: string]: unknown;
    };
    dirty: boolean;
    status: string;
    errors: { [key: string]: unknown };
  };
}

export abstract class BasePaginatedTableState<
  T,
  TState extends BasePaginatedTableStateModel<T> = BasePaginatedTableStateModel<T>,
> extends NgxsStateFacade<TState> {
  abstract searchOnReset: boolean;

  get pageItems(): Immutable<Array<any>> {
    return this.snapshot.pageItems;
  }

  get totalCount(): number {
    return this.snapshot.totalCount;
  }

  get paginatorIndex(): number {
    return this.snapshot.paginatorIndex;
  }

  get pageSize(): number {
    return this.snapshot.pageSize;
  }

  protected constructor(
    store: Store,
    stateName: string,
    protected readonly api: BasePaginatedApiService<T>,
    protected readonly navigate: NavigateService,
  ) {
    super(store, stateName);
  }

  cancel(): void {
    this.navigate.toManagement();
  }

  protected get filters(): { [key: string]: unknown } {
    return Object.fromEntries(
      Object.entries(this.snapshot.filterForm.model).filter(([, value]) =>
        this.notEmpty(value as string | number | undefined | null),
      ),
    );
  }

  private readonly notEmpty = (val: string | number | undefined | null): boolean => {
    if (typeof val === 'number') {
      return true;
    }
    if (typeof val === 'string') {
      return !!val.length;
    }
    return val !== null && val !== undefined;
  };

  protected fetchPage(event?: PageEvent): Observable<Page<T>> {
    return this.api.getPage(event?.pageIndex, event?.pageSize, this.filters);
  }

  protected withPage(state: TState, page: Page<T>): TState {
    return produce(state, (draft) => {
      draft.pageItems = page.items as Array<Draft<T>>;
      draft.pageSize = page.pageSize;
      draft.paginatorIndex = page.pageIndex - 1;
      draft.totalCount = page.totalCount;
    }) as TState;
  }
}
